import { z } from "zod";
import type { CreateOrderRequest, CustomerDetails } from "../types/order";
import { isValidPhone } from "./phone";
import { configInputSchema } from "./config";

const addressSchema = z.object({
  street: z.string().min(1),
  postalCode: z.string().min(4),
  city: z.string().min(1),
  country: z.string().min(2).optional(),
});

export const customerDetailsSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  // Telefoon is verplicht bij het bestellen: het eigen transport meldt de bezorgdag per sms.
  // Zelfde regel als de checkout gebruikt (isValidPhone), zodat client en server niet uiteen
  // lopen. In het type blijft het veld optioneel: oudere orders hebben het niet.
  phone: z.string().refine(isValidPhone, "Vul een geldig telefoonnummer in"),
  companyName: z.string().optional(),
  kvk: z.string().optional(),
  vatNumber: z.string().optional(),
  deliveryMethod: z.enum(["ship", "pickup"]).optional(),
  address: addressSchema.optional(),
  billingAddress: addressSchema.optional(),
});

const orderLineRequestSchema = z.union([
  z.object({
    config: configInputSchema,
    grindLengthMm: z.number().nonnegative().optional(),
    drawingSvg: z.string().max(80000).optional(),
    // Productie-informatie (coördinaten van gaten/uitsparingen) — geen invloed op de prijs,
    // wél op de productietekening die naar de klant en naar Timmers gaat.
    productionOps: z
      .array(
        z.object({
          kind: z.enum(["boring", "uitsparing", "hoek"]),
          label: z.string().max(80),
          positie: z.string().max(40).nullish(),
          xMm: z.number().nonnegative().max(20000).nullish(),
          yMm: z.number().nonnegative().max(20000).nullish(),
          diaMm: z.number().positive().max(1000).nullish(),
          wMm: z.number().positive().max(20000).nullish(),
          hMm: z.number().positive().max(20000).nullish(),
          radiusMm: z.number().nonnegative().max(5000).nullish(),
          hohMm: z.number().positive().max(20000).nullish(),
          slijpen: z.string().max(40).nullish(),
          aantal: z.number().int().positive().max(999).nullish(),
        }),
      )
      .max(120)
      .optional(),
  }),
  z.object({
    articleSlug: z.string().min(1),
    quantity: z.number().int().min(1).max(999),
    note: z.string().trim().max(400).optional(),
    /** Op-lengte-artikel (U-profiel): gewenste lengte in mm; server prijst per meter. */
    lengthMm: z.number().positive().max(20000).optional(),
    /** Voorzetraam (STORM_WINDOW): nominale maat in mm; server herprijst basis + toeslagen. */
    wMm: z.number().positive().max(20000).optional(),
    hMm: z.number().positive().max(20000).optional(),
    /** Offerte-regel (prijs op aanvraag): server rekent €0 in offerte-modus, geen prijs-lookup. */
    quote: z.boolean().optional(),
    /** Naam voor op de order (nodig voor offerte-items zonder catalogus-artikel, bv. BENGglas). */
    name: z.string().trim().max(120).optional(),
  }),
  z.object({
    /** BENGglas (vacuümglas): server herprijst uit productId + maten + opties. */
    benglasProductId: z.string().min(1).max(40),
    widthMm: z.number().positive().max(20000),
    heightMm: z.number().positive().max(20000),
    quantity: z.number().int().min(1).max(999),
    shape: z.enum(["rechthoek", "vorm", "cirkel"]).optional(),
    innerPane: z.enum(["2EVA", "2PVB", "akoestisch", "hybride", "hybride-gelaagd"]).optional(),
    spouwMm: z.number().positive().max(100).optional(),
    spacer: z.enum(["standaard", "warm-edge"]).optional(),
    /** Maatvoeringstekening uit de configurator — zelfde veld als bij enkel glas. Zonder dit
     *  kreeg een vacuümglasorder alleen een specificatieblad en nooit een tekening. */
    drawingSvg: z.string().max(80000).optional(),
  }),
  z.object({
    roosterSlug: z.string().min(1).max(120),
    widthMm: z.number().positive().max(20000),
    colorType: z.string().max(40).optional(),
    ral: z.string().trim().max(12).optional(),
    colorTypeBinnen: z.string().max(40).optional(),
    ralBinnen: z.string().trim().max(12).optional(),
    bediening: z.enum(["koord", "stang"]).optional(),
    zijde: z.enum(["een", "beide"]).optional(),
    /** Stangbediening: kleur + lengte van de stang (moeten op de bestelbon staan). */
    stangColor: z.string().trim().max(60).optional(),
    stangLengthMm: z.number().int().min(100).max(6000).optional(),
    gutter: z.number().nonnegative().max(20000).optional(),
    plaatsing: z.enum(["glas", "kalf"]).optional(),
    quantity: z.number().int().min(1).max(999),
  }),
  z.object({
    /** Douche-opstelling: server herprijst glas + beslag via calculateShowerPrice. */
    showerLayout: z.enum([
      "nis-deur", "deur-zijpaneel", "nis-dubbele-deur", "nis-deur-dubbel-zijlicht",
      "hoekopstelling", "hoek-deur-dubbel-zijlicht",
      "inloop-enkel", "inloop-dubbel",
    ]),
    showerFinish: z.enum(["chroom", "mat-chroom", "zwart", "rvs", "gunmetal", "goud", "brons", "koper"]),
    showerMatGlass: z.boolean().optional(),
    showerGlassColor: z.string().trim().max(40).optional(),
    showerGlassThicknessMm: z.union([z.literal(8), z.literal(10)]).optional(),
    showerDepthMm: z.number().int().min(300).max(2500).optional(),
    showerHingeSide: z.enum(["links", "rechts"]).optional(),
    showerSealDoorOn: z.boolean().optional(),
    showerSealHoekOn: z.boolean().optional(),
    showerHardware: z
      .object({
        scharnier: z.string().max(120).optional(),
        knop: z.string().max(120).optional(),
        stang: z.string().max(120).optional(),
        stangGlas: z.string().max(120).optional(),
        stangWand: z.string().max(120).optional(),
        uProfiel: z.string().max(120).optional(),
        sealDoor: z.string().max(120).optional(),
        sealHoek: z.string().max(120).optional(),
      })
      .optional(),
    heightMm: z.number().positive().max(20000),
    widthsMm: z.array(z.number().positive().max(20000)).min(1).max(4),
    quantity: z.number().int().min(1).max(25),
  }),
  z.object({
    isoPakket: z.string().min(1).max(40),
    isoOuter: z.string().min(1).max(12),
    isoInner: z.string().min(1).max(12),
    isoMiddle: z.string().max(12).optional(),
    isoShapeId: z.string().max(30).optional(),
    isoSpouwMm: z.number().int().min(4).max(30).optional(),
    isoGas: z.string().max(20).optional(),
    isoSpacer: z.string().max(20).optional(),
    isoSiliconen: z.boolean().optional(),
    widthMm: z.number().positive().max(20000),
    heightMm: z.number().positive().max(20000),
    quantity: z.number().int().min(1).max(999),
    /** Maatvoeringstekening uit de configurator — zelfde veld als bij enkel glas. Zonder dit
     *  kreeg een isolatieglasorder alléén een specificatieblad en nooit een tekening. */
    drawingSvg: z.string().max(80000).optional(),
  }),
]);

/** Runtime validation of the checkout payload (used by the createOrder function). */
export const createOrderRequestSchema = z.object({
  lines: z.array(orderLineRequestSchema).min(1),
  customer: customerDetailsSchema,
  quote: z.boolean().optional(),
  referral: z.enum(["proglass"]).optional(),
  /** Kortingscode zoals de klant 'm intikte; de server normaliseert en valideert 'm zelf. */
  couponCode: z.string().trim().max(24).optional(),
  /** GA4 client-/sessie-id van deze bezoeker (voor de serverzijdige aankoopmeting). */
  ga4ClientId: z.string().trim().max(64).optional(),
  ga4SessionId: z.string().trim().max(32).optional(),
  /** Gaf de bezoeker toestemming voor analytische cookies? Zo nee, dan meet de server de aankoop
   *  anoniem (geen sessie, geen gebruikers-ID). `nullish`: een callable maakt van undefined null. */
  consentAnalytics: z.boolean().nullish(),
  /** Google-klik-ID van de advertentie waarlangs de klant binnenkwam (offline conversie-import). */
  clickId: z.string().trim().regex(/^[A-Za-z0-9._-]{10,200}$/).optional(),
  clickIdType: z.enum(["gclid", "gbraid", "wbraid"]).optional(),
  clickAt: z.number().int().positive().optional(),
  customerNote: z.string().trim().max(2000).optional(),
  photos: z.array(z.string().url().max(600)).max(10).optional(),
  /** Levertijd zoals de klant hem in de kassa zag. Hier alleen op vorm gecontroleerd; createOrder
   *  toetst hem daarna tegen de admin-instellingen (zie `Order.promisedLeadTime`). */
  promisedLeadTime: z.string().trim().max(60).nullish(),
});

// Compile-time drift guards against the interfaces.
const _assertCustomer: (x: z.infer<typeof customerDetailsSchema>) => CustomerDetails = (x) => x;
const _assertOrder: (x: z.infer<typeof createOrderRequestSchema>) => CreateOrderRequest = (x) => x;
void _assertCustomer;
void _assertOrder;
